/** Selector de UN cliente con búsqueda (Popover + Input + lista). No había uno: `ClientSelector` del Brain es un Select plano sobre otra fuente y `MemberPickerDialog` es de personas. Fuente: `org.clientsAll`. */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useSearch } from '@/hooks/useSearch';

export interface ClientOption {
  client_id: string;
  display_name: string;
  pod: string | null;
}

export function ClientPicker({ clients, value, onChange, disabled, loading, error, onRetry, id }: {
  clients: readonly ClientOption[];
  value: string | null;
  onChange: (clientId: string) => void;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  id?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = useSearch(clients, q, (c) => `${c.display_name} ${c.client_id} ${c.pod ?? ''}`);
  const selected = clients.find((c) => c.client_id === value) ?? null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button id={id} type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled || loading} className="w-full justify-between font-normal">
          <span className={selected ? '' : 'text-muted-foreground'}>{loading ? t('org.team.client_loading', 'Cargando clientes…') : selected ? selected.display_name : t('org.team.client_placeholder', 'Elegir cliente…')}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-2" align="start">
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('org.team.client_search', 'Buscar cliente…')} className="mb-2" />
        <div className="max-h-64 overflow-y-auto">
          {error && (
            <div className="px-2 py-3 text-sm text-muted-foreground">
              {t('org.home.error', 'No se pudo cargar el Org Service. Reintentá en unos segundos.')}
              {onRetry && <Button type="button" size="sm" variant="outline" className="ml-2" onClick={onRetry}>{t('common.retry', 'Reintentar')}</Button>}
            </div>
          )}
          {!error && filtered.length === 0 && <p className="px-2 py-3 text-sm text-muted-foreground">{t('org.home.no_results', 'Sin resultados')}</p>}
          {filtered.length > 80 && <p className="px-2 py-1 text-xs text-muted-foreground">{t('org.team.client_more', 'Se muestran 80: seguí escribiendo para filtrar.')}</p>}
          {filtered.slice(0, 80).map((c) => (
            <button
              key={c.client_id}
              type="button"
              onClick={() => { onChange(c.client_id); setOpen(false); setQ(''); }}
              className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-muted ${c.client_id === value ? 'bg-muted font-medium' : ''}`}
            >
              <span>{c.display_name}</span>
              {c.pod && <span className="text-xs text-muted-foreground">{c.pod}</span>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
